import React, { useState } from "react";
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@ncoder/ui";
import {
  ChevronDown,
  ChevronRight,
  FileCode,
  Loader2,
} from "lucide-react";
import { WeaveIcon } from "@/components/atoms/WeaveIcon";
import { cn } from "@/lib/utils";
import { BucketDot } from "./BucketBadge";
import { ImpactFindingDetailRow } from "./ImpactFindingDetailRow";
import type { FindingOut, ImpactBucket } from "@/services/salesforce-release";

export interface ComponentImproveArg {
  componentRef: string;
  componentType: string;
  componentName: string;
  findings: FindingOut[];
}

const BUCKET_ORDER: ImpactBucket[] = ["Breaking", "Deprecated", "Review", "Improvement", "OK"];

const ICON_FOR_TYPE: Record<string, string> = {
  ApexClass: "text-purple-500",
  ApexTrigger: "text-orange-500",
  LightningComponentBundle: "text-yellow-500",
  Flow: "text-emerald-500",
  CustomObject: "text-blue-500",
  ValidationRule: "text-indigo-500",
  ReleaseNote: "text-muted-foreground",
};

function countByBucket(findings: FindingOut[]): Record<ImpactBucket, number> {
  const out: Record<ImpactBucket, number> = {
    Breaking: 0, Deprecated: 0, Review: 0, Improvement: 0, OK: 0,
  };
  for (const f of findings) out[f.bucket] += 1;
  return out;
}

interface Props {
  componentRef: string;
  componentType: string;
  componentName: string;
  findings: FindingOut[];
  isExpanded?: boolean;
  onToggle?: (ref: string) => void;
  /** Per-component "Fix with AI" handoff to the multi-agent chat. */
  onImproveWithAI?: (group: ComponentImproveArg) => void;
  isImprovingWithAI?: boolean;
}

export const ComponentRow: React.FC<Props> = ({
  componentRef,
  componentType,
  componentName,
  findings,
  isExpanded: controlled,
  onToggle,
  onImproveWithAI,
  isImprovingWithAI = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlled ?? internalOpen;
  const handleToggle = () => {
    if (onToggle) onToggle(componentRef);
    else setInternalOpen(!internalOpen);
  };

  const iconColor = ICON_FOR_TYPE[componentType] ?? "text-muted-foreground";
  const counts = countByBucket(findings);

  // Best line range to show (first finding with lines)
  const firstLines = findings.find((f) => f.lines && f.lines !== "n/a")?.lines;

  return (
    <Collapsible open={isOpen} onOpenChange={handleToggle}>
      <CollapsibleTrigger asChild>
        <div
          className={cn(
            "flex items-center gap-3 p-3 cursor-pointer transition-colors overflow-hidden",
            "hover:bg-muted/50 border-b border-border/50",
            isOpen && "bg-muted/30"
          )}
        >
          <div className="flex-shrink-0">
            {isOpen ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>

          <div className="flex-shrink-0">
            <FileCode className={cn("h-4 w-4", iconColor)} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium truncate">{componentName}</span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">{componentType}</span>
              {firstLines ? (
                <Badge variant="outline" className="font-mono text-[10px]">
                  L{firstLines}
                </Badge>
              ) : null}
            </div>
          </div>

          {/* Per-bucket count pills, findings badge, and weave button */}
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {BUCKET_ORDER.map((b) => (
              <BucketDot key={b} bucket={b} count={counts[b]} />
            ))}

            <Badge variant="secondary" className="text-xs font-normal">
              {findings.length} {findings.length === 1 ? "finding" : "findings"}
            </Badge>

            {onImproveWithAI ? (
              <Button
                size="sm"
                variant="outline"
                className="hidden sm:inline-flex h-7 gap-1.5"
                disabled={isImprovingWithAI}
                onClick={(e) => {
                  e.stopPropagation();
                  onImproveWithAI({ componentRef, componentType, componentName, findings });
                }}
                title={`Weave in ${componentName}'s findings to AI with Weave`}
              >
                {isImprovingWithAI ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <WeaveIcon className="h-3.5 w-3.5" />
                )}
                <span>{isImprovingWithAI ? "Weaving in…" : "Let's Weave"}</span>
              </Button>
            ) : null}
          </div>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="border-b border-border/50 bg-muted/10">
          {findings.map((f, i) => (
            <ImpactFindingDetailRow
              key={f.finding_id}
              finding={f}
              isLast={i === findings.length - 1}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ComponentRow;
export { BUCKET_ORDER as COMPONENT_ROW_BUCKETS };
